/**
 * What happened on a shopping trip — the one reading of a line's outcome.
 *
 * A shopping list line used to have three states and one report sentence:
 * "0 prices verified, 4 items skipped". "Skipped" covered two unrelated things
 * — a line the shop was out of (nothing to learn, correctly no price) and a
 * line the shopper bought without recording what it cost (everything to learn,
 * and we learned none of it). One number cannot answer both.
 *
 * This module is the authoritative reading of:
 *   - which outcome a line is in,
 *   - whether that outcome is a purchase,
 *   - where a line's price came from, and therefore what it may be used for.
 *
 * It is pure: no DOM, no network, no supabase. The phone's Shopping Guide
 * (`public/js/shopping-guide.js`, a plain <script> with no bundler) carries a
 * mirror of the parts it needs, pinned by
 * `app/javascript/utils/shoppingTripOutcome.parity.test.js`. Change one, change
 * the other — the desk and the aisle must not disagree about what a trip was.
 */

/** Outcomes a line can be in. `unavailable` is the legacy spelling; see below. */
export const OUTCOME = Object.freeze({
  PENDING: 'pending',
  FOUND: 'found',
  SUBSTITUTED: 'substituted',
  NOT_PURCHASED: 'not_purchased',
});

/**
 * `unavailable` predates `not_purchased`. It is a real answer a shopper gave in
 * the aisle and live rows still say it, so it is never rewritten — it is read
 * as "not purchased, because the shop was out". Availability is a REASON; the
 * outcome is that the thing was not bought.
 */
export const LEGACY_UNAVAILABLE = 'unavailable';

export const NOT_PURCHASED_REASONS = Object.freeze([
  { value: 'unavailable', label: 'Unavailable' },
  { value: 'changed_mind', label: 'Changed my mind' },
  { value: 'purchased_elsewhere', label: 'Bought it elsewhere' },
  { value: 'had_enough', label: 'Already had enough' },
  { value: 'other', label: 'Other' },
]);

/** Where a price on a line came from. The distinction the whole feature turns on. */
export const PRICE_SOURCE = Object.freeze({
  ASSUMED: 'planned_assumed',
  ENTERED: 'shopper_entered',
  RECEIPT: 'receipt_verified',
});

const PRICE_SOURCE_LABEL = Object.freeze({
  planned_assumed: 'Expected price',
  shopper_entered: 'Price you entered',
  receipt_verified: 'Receipt verified',
});

/**
 * The outcome of a line, with the legacy spelling folded in.
 * Anything unrecognised is PENDING: an outcome we cannot read is an unanswered
 * question, and an unanswered question must never become an implied purchase.
 */
export function outcomeOf(item) {
  const raw = String(item?.found_status ?? '').trim();
  if (raw === OUTCOME.FOUND) return OUTCOME.FOUND;
  if (raw === OUTCOME.SUBSTITUTED) return OUTCOME.SUBSTITUTED;
  if (raw === OUTCOME.NOT_PURCHASED || raw === LEGACY_UNAVAILABLE) return OUTCOME.NOT_PURCHASED;
  return OUTCOME.PENDING;
}

/** Was this line bought? Only a purchase creates purchase evidence. */
export function isPurchase(item) {
  const outcome = outcomeOf(item);
  return outcome === OUTCOME.FOUND || outcome === OUTCOME.SUBSTITUTED;
}

/** Has the shopper answered for this line at all? */
export function isSettled(item) {
  return outcomeOf(item) !== OUTCOME.PENDING;
}

/** Why a line was not bought, in words. `unavailable` answers for itself. */
export function notPurchasedReasonLabel(item) {
  const stated = String(item?.not_purchased_reason ?? '').trim();
  if (stated) {
    const known = NOT_PURCHASED_REASONS.find((r) => r.value === stated);
    return known ? known.label : stated;
  }
  return String(item?.found_status ?? '') === LEGACY_UNAVAILABLE ? 'Unavailable' : null;
}

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * What this line is believed to have cost, and how much that belief is worth.
 *
 * `source` is the whole point: an expected price copied from the plan totals a
 * trip perfectly well and is worth nothing as evidence about what the vendor
 * charges. Callers that move money must read `isEvidence`, not `amount`.
 */
export function priceEvidence(item) {
  const unit = toNumber(item?.actual_price);
  const source = String(item?.actual_price_source ?? '').trim() || null;
  const qty = toNumber(item?.actual_quantity) ?? toNumber(item?.packages);
  return {
    amount: unit,
    quantity: qty,
    lineTotal: unit === null || qty === null ? null : Number((unit * qty).toFixed(4)),
    source,
    // A price with no stated source predates the column and can only have come
    // from a person typing it, so it counts as evidence.
    isEvidence: unit !== null && unit > 0 && source !== PRICE_SOURCE.ASSUMED,
    isAssumed: source === PRICE_SOURCE.ASSUMED,
    isReceiptVerified: source === PRICE_SOURCE.RECEIPT,
    label: source ? PRICE_SOURCE_LABEL[source] ?? null : null,
  };
}

/** True when this line records a substitution, however completely identified. */
export function isSubstituted(item) {
  return outcomeOf(item) === OUTCOME.SUBSTITUTED
    || !!item?.substitute_purchase_option_id
    || !!item?.substitute_inventory_item_id
    || !!String(item?.substitute_description ?? '').trim();
}

/** What was actually bought on a substituted line, in words. */
export function substituteLabel(item) {
  return String(item?.substitute_description ?? '').trim() || null;
}

/**
 * The trip, counted by what happened rather than by what we could learn from
 * it. `pending` is deliberately its own number and is never folded into either
 * of the others: it is the count of questions nobody answered.
 */
export function summarizeTrip(items) {
  const list = Array.isArray(items) ? items : [];
  const summary = {
    total: list.length,
    purchased: 0,
    substituted: 0,
    notPurchased: 0,
    pending: 0,
    expectedTotal: 0,
    reportedTotal: 0,
    pricesAssumed: 0,
    pricesEntered: 0,
    pricesReceiptVerified: 0,
    pricesMissing: 0,
    hasAnyReportedMoney: false,
  };

  list.forEach((item) => {
    const outcome = outcomeOf(item);
    if (outcome === OUTCOME.FOUND) summary.purchased += 1;
    else if (outcome === OUTCOME.SUBSTITUTED) summary.substituted += 1;
    else if (outcome === OUTCOME.NOT_PURCHASED) summary.notPurchased += 1;
    else summary.pending += 1;

    const planned = toNumber(item?.estimated_line_total);
    const plannedFallback = (toNumber(item?.packages) ?? 0) * (toNumber(item?.estimated_price) ?? 0);
    summary.expectedTotal += planned === null ? plannedFallback : planned;

    if (outcome !== OUTCOME.FOUND && outcome !== OUTCOME.SUBSTITUTED) return;

    const price = priceEvidence(item);
    if (price.amount === null) summary.pricesMissing += 1;
    else if (price.isAssumed) summary.pricesAssumed += 1;
    else if (price.isReceiptVerified) summary.pricesReceiptVerified += 1;
    else summary.pricesEntered += 1;

    if (price.lineTotal !== null) {
      summary.reportedTotal += price.lineTotal;
      summary.hasAnyReportedMoney = true;
    }
  });

  summary.expectedTotal = Number(summary.expectedTotal.toFixed(2));
  summary.reportedTotal = summary.hasAnyReportedMoney ? Number(summary.reportedTotal.toFixed(2)) : null;
  summary.purchaseLines = summary.purchased + summary.substituted;
  return summary;
}

/**
 * The review shown before a trip is finished, in the three sections the report
 * will use afterwards. Unanswered lines lead, because the point of the step is
 * that finishing is never accidental.
 *
 * `blockingCount` is what the confirm copy warns about. It is deliberately NOT
 * a hard block: you genuinely might not have got to something.
 */
export function buildTripReview(items) {
  const list = Array.isArray(items) ? items : [];
  const unresolved = list.filter((item) => !isSettled(item));
  const purchased = list.filter((item) => outcomeOf(item) === OUTCOME.FOUND);
  const substituted = list.filter((item) => outcomeOf(item) === OUTCOME.SUBSTITUTED);
  const notPurchased = list.filter((item) => outcomeOf(item) === OUTCOME.NOT_PURCHASED);
  return {
    summary: summarizeTrip(list),
    unresolved,
    purchased,
    substituted,
    notPurchased,
    blockingCount: unresolved.length,
    allSettled: unresolved.length === 0,
  };
}

const RECONCILIATION_LABEL = Object.freeze({
  exact_match: 'Matches the receipt',
  quantity_changed: 'Quantity differs from the receipt',
  price_changed: 'Price differs from the receipt',
  substitute_confirmed: 'Substitution confirmed',
  substitute_differs: 'Receipt shows a different substitute',
  not_marked_by_shopper: 'On the receipt, never marked',
  marked_but_absent: 'Marked bought, not on the receipt',
  unplanned: 'Not on the shopping list',
  unresolved: 'Could not be matched',
});

export function reconciliationLabel(status) {
  const key = String(status ?? '').trim();
  return key ? RECONCILIATION_LABEL[key] ?? key : null;
}

/** A disagreement the user should look at, as opposed to a clean match. */
export function isReconciliationDiscrepancy(status) {
  const key = String(status ?? '').trim();
  return key !== '' && key !== 'exact_match' && key !== 'substitute_confirmed';
}

/**
 * The receipt half of the report. Counts are over receipt lines, so an
 * unplanned line is visible rather than quietly dropped.
 */
export function summarizeReconciliation(lines) {
  const list = Array.isArray(lines) ? lines : [];
  const summary = {
    matched: 0,
    pricesVerified: 0,
    priceChanges: 0,
    quantityChanges: 0,
    substitutionsConfirmed: 0,
    unplanned: 0,
    unresolved: 0,
    discrepancies: 0,
    receiptTotal: 0,
    hasTotal: false,
  };
  list.forEach((line) => {
    const status = String(line?.reconciliation_status ?? '').trim();
    if (status && status !== 'unresolved' && status !== 'unplanned') summary.matched += 1;
    if (status === 'exact_match') summary.pricesVerified += 1;
    if (status === 'price_changed') { summary.priceChanges += 1; summary.pricesVerified += 1; }
    if (status === 'quantity_changed') summary.quantityChanges += 1;
    if (status === 'substitute_confirmed') summary.substitutionsConfirmed += 1;
    if (status === 'unplanned') summary.unplanned += 1;
    if (status === 'unresolved') summary.unresolved += 1;
    if (isReconciliationDiscrepancy(status)) summary.discrepancies += 1;
    const ext = toNumber(line?.extended_amount);
    if (ext !== null) { summary.receiptTotal += ext; summary.hasTotal = true; }
  });
  summary.receiptTotal = summary.hasTotal ? Number(summary.receiptTotal.toFixed(2)) : null;
  return summary;
}
